/**
 * Fabric Embed Mode Authentication
 *
 * When the IntuigenceAI frontend is embedded in a Fabric workload iframe,
 * authentication is handled via PostMessage rather than Keycloak.
 *
 * The parent Fabric window sends AUTH_TOKEN messages containing an Azure AD
 * token. This module stores and provides that token for API calls.
 */

// PostMessage types (must match FabricPostMessageProtocol.ts in the Fabric workload)
export enum FabricMessageType {
  APP_READY = 'APP_READY',
  AUTH_TOKEN = 'AUTH_TOKEN',
  AUTH_TOKEN_EXPIRED = 'AUTH_TOKEN_EXPIRED',
  AUTH_TOKEN_REFRESH = 'AUTH_TOKEN_REFRESH',
  FABRIC_CONTEXT = 'FABRIC_CONTEXT',
  FABRIC_THEME = 'FABRIC_THEME',
  APP_ERROR = 'APP_ERROR',
}

interface FabricMessage<T = unknown> {
  type: FabricMessageType
  payload: T
}

interface AuthTokenPayload {
  token: string
  workspaceId?: string
}

export const ALLOWED_ORIGINS: string[] = [
  'https://app.fabric.microsoft.com',
  'https://app.powerbi.com',
  // Deployed Fabric workload hosts (one per env). The bundle is shared across envs,
  // so ALL hosts must be listed or embed-mode postMessage auth silently fails there.
  // Kept in sync with fabric/env-config.json by the fabric-ci drift cross-check.
  'https://fabric.dev.aws.intuigence.ai',
  'https://fabric.staging.aws.intuigence.ai',
  // GCP dev/staging hosts: kept only until the GCP us-central1 dev/staging decommission
  // removes them, so the live workloads keep authenticating through the cutover.
  'https://fabric.dev.gcp.intuigence.ai',
  'https://fabric.staging.gcp.intuigence.ai',
  'https://fabric.demo.intuigence.ai',
  // The fork's still-live PROD workload (IntuigenceAI.IndustrialWorkers) is hosted here
  // (Azure Static Web App) and points at the dev backend. It must stay allow-listed until
  // prod is migrated off the fork — removing it (done prematurely in the monorepo migration)
  // breaks embed-mode postMessage auth for the live prod workload. Remove on fork decommission.
  'https://fabric-dev.intuigence.ai',
]
// Allow localhost origins in development
if (process.env.NODE_ENV === 'development') {
  ALLOWED_ORIGINS.push('http://localhost:60006', 'http://127.0.0.1:60006')
}

let embedToken: string | null = null
let embedWorkspaceId: string | null = null
let isEmbedMode = false
let messageListenerAttached = false
let tokenResolver: ((token: string) => void) | null = null
let tokenPromise: Promise<string> | null = null
let parentOrigin: string | null = null

/** Check if the app is running in Fabric embed mode. */
export function isFabricEmbedMode(): boolean {
  return isEmbedMode
}

/** Get the current embed token (or null if not in embed mode or no token yet). */
export function getEmbedToken(): string | null {
  return embedToken
}

/** Get the Fabric workspace ID passed via PostMessage. */
export function getEmbedWorkspaceId(): string | null {
  return embedWorkspaceId
}

/** Get the verified parent origin (set after first AUTH_TOKEN from a valid origin). */
export function getParentOrigin(): string | null {
  return parentOrigin
}

/**
 * Wait for the embed auth token to arrive via PostMessage.
 * Returns immediately if the token is already available.
 * Rejects after timeoutMs if no token arrives.
 */
export function waitForEmbedToken(timeoutMs = 10000): Promise<string> {
  if (embedToken) return Promise.resolve(embedToken)
  if (!tokenPromise) {
    tokenPromise = new Promise<string>((resolve, reject) => {
      tokenResolver = resolve
      setTimeout(() => {
        if (!embedToken) {
          tokenResolver = null
          tokenPromise = null
          reject(new Error('Embed auth token not received within timeout'))
        }
      }, timeoutMs)
    })
  }
  return tokenPromise
}

/** Set embed mode (called on app initialization when ?embed=fabric is detected). */
export function enableFabricEmbedMode(): void {
  isEmbedMode = true
  attachMessageListener()
  signalAppReady()
}

/** Post a message to the parent window using the verified parent origin. */
function postToParent(message: FabricMessage): void {
  if (!window.parent || window.parent === window) return
  // Use the verified parent origin if available, otherwise try production origin
  const targetOrigin = parentOrigin || ALLOWED_ORIGINS[0]!
  window.parent.postMessage(message, targetOrigin)
}

/** Send APP_READY signal to parent window. */
function signalAppReady(): void {
  // APP_READY is sent before we know the parent origin — use '*' for this
  // initial handshake only. All subsequent messages use the verified origin.
  if (window.parent && window.parent !== window) {
    const message: FabricMessage = {
      type: FabricMessageType.APP_READY,
      payload: {},
    }
    window.parent.postMessage(message, '*')
  }
}

/** Send AUTH_TOKEN_EXPIRED signal to parent window. */
export function signalTokenExpired(): void {
  if (!isEmbedMode) return
  postToParent({
    type: FabricMessageType.AUTH_TOKEN_EXPIRED,
    payload: {},
  })
}

/** Attach PostMessage listener for auth tokens from parent. */
function attachMessageListener(): void {
  if (messageListenerAttached) return
  messageListenerAttached = true

  window.addEventListener('message', (event: MessageEvent) => {
    // Validate origin — only accept messages from allowed Fabric origins
    if (!ALLOWED_ORIGINS.includes(event.origin)) {
      return
    }

    const message = event.data as FabricMessage
    if (!message?.type) return

    switch (message.type) {
      case FabricMessageType.AUTH_TOKEN:
      case FabricMessageType.AUTH_TOKEN_REFRESH: {
        const payload = message.payload as AuthTokenPayload
        if (payload?.token) {
          // Store the verified parent origin for future outbound messages
          parentOrigin = event.origin
          embedToken = payload.token
          if (payload.workspaceId) {
            embedWorkspaceId = payload.workspaceId
          }
          // Resolve any callers waiting for the initial token
          if (tokenResolver) {
            tokenResolver(payload.token)
            tokenResolver = null
            tokenPromise = null
          }
        }
        break
      }
    }
  })
}
